import { deprecated, QBadge, QCheckbox, QSelect, QSelectItem, QTag } from '@qualio/ui-components';
import React from 'react';
import { StopPropagation } from '../../../../utils/events';
import { getAssociationStatus } from '../../../../utils/translator';
import { buildDispatchObject, EntityDispatch } from '../../state';
import {
  AllCategories,
  PotentialNewOwners,
  ReassignChangeRequest,
  ReassignChangeRequestsMap,
  ReassignControl,
  ReassignControlsMap,
  ReassignDocument,
  ReassignDocumentsMap,
  ReassignDocumentTemplate,
  ReassignDocumentTemplatesMap,
  ReassignEventTemplate,
  ReassignEventTemplatesMap,
  ReassignItems,
  ReassignItemsMap,
} from '../../types';

type CheckBoxChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

// Builds the column data for both "item" and "doc" tables
export const BuildColumns = (
  category: AllCategories,
  allSelected: boolean,
  handleHeaderCheckboxChange: CheckBoxChangeHandler,
  t: (key: string) => string,
): readonly deprecated.QDataColumn[] => {
  const tableTypeByCategory = {
    owner: 'doc',
    reviewer: 'doc',
    approver: 'doc',
    changeRequestOwner: 'item',
    issues: 'item',
    tasks: 'item',
    default_owner: 'event_template',
    eventTemplateOwner: 'event_template',
    control: 'control',
    documentTemplateOwner: 'document_template',
  } as const;
  const tableType = tableTypeByCategory[category];

  const checkBoxColumn: deprecated.QDataColumn = {
    Header: (
      <QCheckbox
        aria-label="table_checkbox"
        size="md"
        isChecked={allSelected}
        onClick={StopPropagation}
        onChange={handleHeaderCheckboxChange}
      />
    ),
    disableSortBy: true,
    width: '2%',
    accessor: 'checkbox',
  };
  const idColumn: deprecated.QDataColumn = {
    Header: t('reassignEntities.columns.id'),
    width: '5%',
    accessor: 'itemCodeOrPrefix',
    sortType: 'alphanumeric',
    Cell: ({ row }: { row: { values: { itemCodeOrPrefix: string } } }) => (
      <QBadge>{row.values.itemCodeOrPrefix}</QBadge>
    ),
  };
  const titleColumn: deprecated.QDataColumn = {
    Header: t('reassignEntities.columns.title'),
    accessor: 'titleOrName',
    width: tableType === 'item' ? '18%' : '12%',
  };
  const newOwnerColumn: deprecated.QDataColumn = {
    Header: t('reassignEntities.columns.newOwner'),
    accessor: 'newOwner',
    width: tableType === 'item' ? '5%' : '15%',
  };
  const statusColumn: deprecated.QDataColumn = {
    Header: t('reassignEntities.columns.status'),
    accessor: 'status',
    width: '10%',
  };
  const tagColumn: deprecated.QDataColumn = {
    Header: t('reassignEntities.columns.tags'),
    accessor: 'tags',
    width: '5%',
  };
  const columnsByTableType: Record<string, readonly deprecated.QDataColumn[]> = {
    item: [checkBoxColumn, idColumn, titleColumn, newOwnerColumn],
    doc: [checkBoxColumn, idColumn, titleColumn, statusColumn, tagColumn, newOwnerColumn],
    event_template: [
      checkBoxColumn,
      { ...idColumn, Header: t('reassignEntities.columns.prefix') },
      { ...titleColumn, Header: t('reassignEntities.columns.name') },
      newOwnerColumn,
    ],
    control: [checkBoxColumn, idColumn, titleColumn, newOwnerColumn],
    document_template: [
      checkBoxColumn,
      { ...idColumn, Header: t('reassignEntities.columns.prefix') },
      { ...titleColumn, Header: t('reassignEntities.columns.name') },
      statusColumn,
      newOwnerColumn,
    ],
  } as const;

  return columnsByTableType[tableType];
};

const getNeededValues = (
  entity:
    | ReassignDocument
    | ReassignItems
    | ReassignChangeRequest
    | ReassignEventTemplate
    | ReassignControl
    | ReassignDocumentTemplate,
  category: AllCategories,
) => {
  const neededValues = {
    id: entity.id,
    newOwner: entity.newOwner,
    codeOrPrefix: '',
    titleOrName: '',
  };

  if ('code' in entity) {
    neededValues.codeOrPrefix = entity.code.toString();
    if (category === 'control' && 'instance_id' in entity) {
      neededValues.codeOrPrefix = entity.instance_id ? `C-${entity.code.toString()}` : `Q-${entity.code.toString()}`;
    }
  } else if ('prefix' in entity) {
    neededValues.codeOrPrefix = entity.prefix;
  }

  if ('title' in entity) {
    neededValues.titleOrName = entity.title;
  } else if ('name' in entity) {
    neededValues.titleOrName = entity.name;
  }

  return neededValues;
};

// Builds the row data for "item", "doc", and "event template" tables
export const BuildRows = (
  category: AllCategories,
  dispatch: EntityDispatch,
  entities:
    | ReassignDocumentsMap
    | ReassignItemsMap
    | ReassignChangeRequestsMap
    | ReassignDocumentTemplatesMap
    | ReassignEventTemplatesMap
    | ReassignControlsMap,
  selected: Set<number>,
  setSelected: (selectedItems: Set<number>) => void,
  users: PotentialNewOwners[],
): deprecated.QDataRow[] => {
  return Object.keys(entities).map((entityId) => {
    const qualioEntity = entities[entityId];

    const { id, newOwner, codeOrPrefix, titleOrName } = getNeededValues(qualioEntity, category);
    const currentValue = newOwner ? newOwner.id.toString() : undefined;

    const selectBoxChangeHandler = (changedItem: QSelectItem) => {
      const newMap = {
        ...entities,
        [id]: {
          ...qualioEntity,
          newOwner: {
            id: Number(changedItem.value),
            name: changedItem.label,
          },
        },
      };

      dispatch(buildDispatchObject(category, newMap));
    };

    const checkboxChangeHandler = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
      const newSelected = new Set([...selected]);

      if (changeEvent.currentTarget.checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }

      setSelected(newSelected);
    };

    const baseRow = {
      checkbox: (
        <QCheckbox
          aria-label="table_checkbox"
          size="md"
          isChecked={selected.has(id)}
          onChange={checkboxChangeHandler}
        />
      ),
      id,
      itemCodeOrPrefix: codeOrPrefix,
      titleOrName,
      newOwner: (
        <QSelect
          aria-label="documents_single_select"
          value={currentValue}
          options={users}
          onChange={(item) => item && selectBoxChangeHandler(item)}
        />
      ),
    };

    if (
      category === 'issues' ||
      category === 'tasks' ||
      category === 'changeRequestOwner' ||
      category === 'default_owner' ||
      category === 'eventTemplateOwner' ||
      category === 'control'
    ) {
      return baseRow;
    }

    if (category === 'documentTemplateOwner') {
      const { status } = qualioEntity as ReassignDocumentTemplate;

      return {
        ...baseRow,
        status: getAssociationStatus(status),
      };
    }

    const { status_id: statusId, tags } = qualioEntity as ReassignDocument;

    return {
      ...baseRow,
      status: getAssociationStatus(statusId),
      tags: (
        <>
          {tags.map((tag) => (
            <QTag key={tag.id}>{tag.name}</QTag>
          ))}
        </>
      ),
    };
  });
};
